import { useState, useEffect, useCallback, KeyboardEventHandler } from 'react';
import { TablePaginationProps } from '@mui/material/TablePagination';
import { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { DataGridProps } from '@mui/x-data-grid';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import useDictionary from '@ui/hooks/useDictionary';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';

export interface IErrorMap {
    deviceName: string[];
    imei: string[];
    password: string[];
    group: string[];
}

export interface IRow {
    id: number;
    deviceName: string;
    imei: string;
    password: string;
    group: string;
}

export interface IColumn {
    field: string;
    headerName: string;
    width: number;
    minWidth: number;
    flex: number;
    editable: boolean;
    sortable: boolean;
    filterable: boolean;
}

export interface IState {
    loading: boolean;
    page: number;
    limit: number;
    rows: IRow[];
    columns: IColumn[];
    uploadError: boolean;
    errorMap: IErrorMap;
}

const useAddDeviceTable = (onClose: ICloseEvent['onClose']) => {
    const { download, lang, devices, groups } = useStore();
    const getWord = useDictionary();
    const key = useKeyDown();
    const devicesToAdd = download.uploadedDevices ?? [];

    const colData = [
        { field: 'deviceName', width: 130 },
        { field: 'imei', width: 130 },
        { field: 'password', width: 130 },
        { field: 'group', width: 130 },
    ];
    const columns: IColumn[] = colData.map(({ field, width }) => {
        let headerName = getWord(field);
        return {
            field,
            headerName,
            width,
            minWidth: width,
            flex: 1,
            editable: true,
            sortable: false,
            filterable: false,
        };
    });
    const rows = devicesToAdd.map((data, i) => {
        const id = i;
        const { deviceName, imei, password, group } = data;
        return {
            id,
            deviceName,
            imei,
            password,
            group,
        };
    });

    const initState: IState = {
        loading: false,
        page: 0,
        limit: 50,
        columns,
        rows,
        uploadError: false,
        errorMap: {
            deviceName: [],
            imei: [],
            password: [],
            group: [],
        },
    };
    const [state, setState] = useState(initState);

    const deviceNameValidate = (name: string) => {
        if (!name) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    deviceName: [...prev.errorMap.deviceName, name],
                },
            }));
            return false;
        } else return true;
    };
    const imeiValidate = (imei: string) => {
        const regExp = /^\d{15,16}$/;
        const imeiList = devices.deviceList.map(({ imei }) => imei);
        const doubledImei = state.rows
            .map(({ imei }) => imei)
            .filter((imei, i, arr) => arr.indexOf(imei) !== i)
            .filter((imei, i, arr) => arr.indexOf(imei) === i);
        if (
            !imei ||
            !regExp.test(imei) ||
            imeiList.indexOf(imei) > -1 ||
            doubledImei.indexOf(imei) > -1
        ) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    imei: [...prev.errorMap.imei, imei],
                },
            }));
            return false;
        } else return true;
    };
    const passwordValidate = (password: string) => {
        if (!password) {
            setState((prev) => ({
                ...prev,
                errorMap: {
                    ...prev.errorMap,
                    password: [...prev.errorMap.password, password],
                },
            }));
            return false;
        } else return true;
    };
    const handlePageChange: TablePaginationProps['onPageChange'] = useCallback(
        (e, page) => {
            setState((prev) => ({ ...prev, page }));
        },
        [],
    );
    const handleCancelClick = useCallback(() => {
        onClose();
    }, []);
    const handleAddClick = useCallback(async () => {
        const isValid = state.rows
            .map((device) => {
                const { deviceName, imei } = device;
                const isDeviceNameValid = deviceNameValidate(deviceName);
                const isImeiValid = imeiValidate(imei);
                return isDeviceNameValid && isImeiValid;
            })
            .every((val) => val);
        if (isValid) {
            devices.resetErrorDevice();
            const newGroups = state.rows
                .map((device) => {
                    const { group: groupName } = device;
                    const [group] = groups.groupsList.filter(
                        (group) => group.name === groupName,
                    );
                    if (!group) {
                        return groupName;
                    }
                })
                .filter((name, i, arr) => !!name && arr.indexOf(name) === i);
            setState((prev) => ({ ...prev, loading: true }));
            if (newGroups.length > 0) {
                await Promise.all(
                    newGroups.map(async (group) => {
                        return await groups.createGroupWithoutLoading(group!);
                    }),
                );
                await groups.getUserGroups();
            }
            await Promise.all(
                state.rows.map(async (device) => {
                    const {
                        deviceName,
                        imei,
                        password,
                        group: groupName,
                    } = device;
                    const name = deviceName;
                    const [group] = groups.groupsList.filter(
                        (group) => group.name === groupName,
                    );
                    const groupId = group?.id;
                    const args = {
                        imei,
                        name,
                        password: `${password}`,
                        groupId,
                    };
                    return await devices.addDevice(args);
                }),
            );
            setState((prev) => ({ ...prev, loading: false }));
            const { imeiNotExistError, noRightsError, errorDevices } = devices;
            const isError = imeiNotExistError || noRightsError;
            if (isError) {
                setState((prev) => ({
                    ...prev,
                    rows: prev.rows
                        .filter(({ imei }) => errorDevices.indexOf(imei) > -1)
                        .map((item, id) => ({ ...item, id })),
                    errorMap: {
                        ...prev.errorMap,
                        imei: [...errorDevices],
                    },
                }));
            } else {
                devices.resetOperationSuccess();
                onClose();
            }
        } else setState((prev) => ({ ...prev, uploadError: true }));
    }, [state]);
    const handleCellEditStart = useCallback(() => {
        setState((prev) => ({
            ...prev,
            errorMap: initState.errorMap,
            uploadError: false,
        }));
        devices.resetImeiNotExistError();
        devices.resetNoRightsError();
    }, []);
    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> =
        useCallback((e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                const target = e.target as HTMLButtonElement;
                target.click();
            }
        }, []);
    const handleKey = (key: string) => {
        switch (key) {
            case 'Escape':
                onClose();
                break;
            case 'Enter':
                handleAddClick();
                break;
        }
    };
    const handeLimitChange = useCallback(
        ({ target }: SelectChangeEvent<number>) => {
            let limit: number;
            const { value } = target;
            if (typeof value === 'number') {
                limit = value;
            } else if (typeof value === 'string') {
                const regExp = /^(\d)*/;
                if (!regExp.test(value)) return;
                limit = parseInt(value, 10);
                if (isNaN(limit)) return;
            } else {
                return;
            }
            setState((prev) => ({ ...prev, page: 0, limit }));
        },
        [],
    );
    const handlers = {
        handlePageChange,
        handleCancelClick,
        handleAddClick,
        handleCellEditStart,
        handleBtnKeyDown,
        handeLimitChange,
    };

    useEffect(() => {
        return () => {
            devices.resetImeiNotExistError();
            devices.resetNoRightsError();
            download.resetUploadedDevices();
            devices.resetErrorDevice();
        };
    }, []);
    useEffect(() => {
        handleKey(key);
    }, [key]);

    return {
        download,
        devices,
        lang,
        state,
        handlers,
    };
};

export default useAddDeviceTable;
